import { Container } from 'aurelia-framework';
import { ComponentContext } from './ComponentContext';

export interface ContainerContextDescriptor<TState extends object> {
  contextName: string;
  contextKey: symbol;
  /**
   * Find and return the nearest context of this type in the Container hierarchy
   *
   * @param container The container to start searching from
   * @returns The context instance or null if not found
   *
   * @example
   * ```typescript
   * bind() {
   *   this.context = MyContext.useContext(this.container);
   *   if (this.context) {
   *     this.unsubscribe = this.context.subscribe(state => {
   *       console.log('State:', state);
   *     });
   *   }
   * }
   * ```
   */
  useContext(container: Container): ComponentContext<TState> | null;
  /**
   * Decorator to mark a component as providing this context type via Container
   *
   * Requirements:
   * - Component must inject Container in constructor
   * - Component must have a 'context' property of type ComponentContext<TState>
   *
   * The decorator registers the context in the component's container during bind() lifecycle,
   * making it available to all child components in the hierarchy.
   *
   * @example
   * ```typescript
   * @MyContext.provideContext()
   * @autoinject
   * export class MyComponent {
   *   private context: ComponentContext<MyState>;
   *
   *   constructor(private container: Container) {
   *     this.context = new ComponentContext<MyState>({ status: 'loading' });
   *   }
   * }
   * ```
   */
  provideContext(): ClassDecorator;
  /**
   * Create a new context instance with the given initial state.
   * This is a helper method to ensure the correct typing and structure of the context.
   * This is to be used on the provider component to create the context instance that will be registered in the container.
   *
   * @param initialState initial state of the state
   */
  createContext(initialState: TState): ComponentContext<TState>;
}

/**
 * Create a typed context using Aurelia's Container hierarchy.
 * This approach doesn't depend on DOM and works from bind() lifecycle onwards.
 *
 * Usage:
 * ```typescript
 * // Define context
 * const MyContext = createContainerContext<MyState>(class MyContext {});
 *
 * // Provider
 * @MyContext.provideContext()
 * class Parent {
 *   private context = new ComponentContext<MyState>({...});
 *   constructor(private container: Container) {}
 * }
 *
 * // Consumer
 * class Child {
 *   private context: ComponentContext<MyState>;
 *   constructor(private container: Container) {}
 *
 *   bind() {
 *     this.context = MyContext.useContext(this.container);
 *     this.context?.subscribe(state => {...});
 *   }
 * }
 * ```
 *
 * @param contextClass Class whose name becomes the context identifier
 * @returns Context descriptor with typed methods
 */
export function createContext<TState extends object>(contextClass: new (...args: any[]) => any): ContainerContextDescriptor<TState> {
  const contextName = contextClass.name;
  const contextKey = Symbol.for(`Context_${contextName}`);

  return {
    contextName,
    contextKey,

    /**
     * Find and return the nearest context of this type in the Container hierarchy
     *
     * @param container The container to start searching from
     * @returns The context instance or null if not found
     *
     * @example
     * ```typescript
     * bind() {
     *   this.context = MyContext.useContext(this.container);
     *   if (this.context) {
     *     this.unsubscribe = this.context.subscribe(state => {
     *       console.log('State:', state);
     *     });
     *   }
     * }
     * ```
     */
    useContext(container: Container): ComponentContext<TState> | null {
      if (!container) {
        console.warn(`useContext(${contextName}): container is null or undefined`);
        return null;
      }

      // Walk up the container parent chain to find context
      let currentContainer: Container = container;
      let depth = 0;
      const maxDepth = 50; // Safety limit to prevent infinite loops

      while (currentContainer && depth < maxDepth) {
        // Check if this container has the context registered
        if (currentContainer.hasResolver(contextKey)) {
          try {
            const context = currentContainer.get(contextKey) as ComponentContext<TState>;
            return context;
          } catch (error) {
            console.error(`useContext(${contextName}): Error retrieving context from container at depth ${depth}`, error);
          }
        }

        // Move to parent container
        currentContainer = currentContainer.parent as Container;
        depth++;
      }

      if (depth >= maxDepth) {
        console.error(`useContext(${contextName}): Max container depth reached (${maxDepth})`);
      } else {
        console.warn(`useContext(${contextName}): No context found in container hierarchy (searched ${depth} levels)`);
      }

      return null;
    },

    /**
     * Decorator to mark a component as providing this context type via Container
     *
     * Requirements:
     * - Component must inject Container in constructor
     * - Component must have a 'context' property of type ComponentContext<TState>
     *
     * The decorator registers the context in the component's container during bind() lifecycle,
     * making it available to all child components in the hierarchy.
     *
     * @example
     * ```typescript
     * @MyContext.provideContext()
     * @autoinject
     * export class MyComponent {
     *   private context: ComponentContext<MyState>;
     *
     *   constructor(private container: Container) {
     *     this.context = new ComponentContext<MyState>({ status: 'loading' });
     *   }
     * }
     * ```
     */
    provideContext(): ClassDecorator {
      return function (target: any) {
        const originalBind = target.prototype.bind;
        const originalUnbind = target.prototype.unbind;

        target.prototype.bind = function (bindingContext?: any, overrideContext?: any) {
          if (!this.container) {
            console.error(
              `provideContext(${contextName}): container is undefined. ` +
                `Did you inject Container in the constructor?\n` +
                `Example: constructor(private container: Container) {}`
            );
          } else if (!this.context) {
            console.error(
              `provideContext(${contextName}): context is undefined. ` +
                `Did you create a ComponentContext instance?\n` +
                `Example: this.context = new ComponentContext<${contextName}State>({...})`
            );
          } else {
            // Register context instance in this component's container
            // This makes it available to all child components
            try {
              this.container.registerInstance(contextKey, this.context);
              console.log(`provideContext(${contextName}): Context registered in container`);
            } catch (error) {
              console.error(`provideContext(${contextName}): Error registering context`, error);
            }
          }

          if (originalBind) {
            return originalBind.call(this, bindingContext, overrideContext);
          }
        };

        // Clean up on unbind (optional, but good practice)
        target.prototype.unbind = function () {
          if (this.container && this.container.hasResolver(contextKey)) {
            try {
              // Note: Aurelia's Container doesn't have unregister
              // The registration will be cleaned up when container is disposed
              console.log(`provideContext(${contextName}): Component unbinding (context remains in container)`);
            } catch (error) {
              console.error(`provideContext(${contextName}): Error during unbind`, error);
            }
          }

          if (originalUnbind) {
            return originalUnbind.call(this);
          }
        };

        return target;
      };
    },

    createContext: function (initialState: TState): ComponentContext<TState> {
      return new ComponentContext<TState>(initialState);
    },
  };
}

/**
 * Helper to check if a container or any of its parents has a specific context
 * Useful for conditional logic based on context availability
 *
 * @example
 * ```typescript
 * if (hasContext(this.container, MyContext)) {
 *   const ctx = MyContext.useContext(this.container);
 *   // Use context
 * } else {
 *   // Fallback behavior
 * }
 * ```
 */
export function hasContext<TState extends object>(container: Container, contextDescriptor: ContainerContextDescriptor<TState>): boolean {
  if (!container) return false;

  let currentContainer: Container = container;
  let depth = 0;
  const maxDepth = 50;

  while (currentContainer && depth < maxDepth) {
    if (currentContainer.hasResolver(contextDescriptor.contextKey)) {
      return true;
    }
    currentContainer = currentContainer.parent as Container;
    depth++;
  }

  return false;
}

/**
 * Helper to get context with a default value if not found
 * Prevents need for null checks in consuming components
 *
 * @example
 * ```typescript
 * bind() {
 *   this.context = getContextOrDefault(
 *     this.container,
 *     MyContext,
 *     new ComponentContext<MyState>({ status: 'default' })
 *   );
 * }
 * ```
 */
export function getContextOrDefault<TState extends object>(
  container: Container,
  contextDescriptor: ContainerContextDescriptor<TState>,
  defaultContext: ComponentContext<TState>
): ComponentContext<TState> {
  const context = contextDescriptor.useContext(container);
  return context !== null ? context : defaultContext;
}
